import { type NavigationDeliveryEntry } from './navigation-delivery-store';
// The v1 key predates unconfirmed entries; its stored shape is unchanged.
export const NAVIGATION_DELIVERY_STORAGE_KEY =
  'imai:navigation-delivery-failures:v1';
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const statuses: ReadonlySet<unknown> = new Set([
  'pending',
  'unconfirmed',
  'failed',
]);

export function readNavigationDeliveries(
  storage: Pick<Storage, 'getItem'>
): NavigationDeliveryEntry[] {
  try {
    const parsed: unknown = JSON.parse(
      storage.getItem(NAVIGATION_DELIVERY_STORAGE_KEY) ?? '[]'
    );
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is NavigationDeliveryEntry => {
      if (!record(entry) || !statuses.has(entry.status) || !record(entry.job))
        return false;
      const job = entry.job;
      return (
        [
          'id',
          'actorId',
          'orgSlug',
          'routeRef',
          'orgId',
          'title',
          'prompt',
        ].every((key) => typeof job[key] === 'string') &&
        (job.visibility === 'private' || job.visibility === 'organization') &&
        record(job.options)
      );
    });
  } catch {
    return [];
  }
}

/**
 * Persist each entry with its real status. A reload cannot tell whether the
 * server committed an in-flight send, so a stored `pending` entry is restored
 * as unconfirmed and checked against server receipts; it is never shown as
 * failed or replayed automatically on the strength of storage alone.
 */
export function writeNavigationDeliveries(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  entries: readonly NavigationDeliveryEntry[]
) {
  try {
    storage.setItem(NAVIGATION_DELIVERY_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* The app-owned copy remains available when browser storage is full. */
  }
}
